import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { SourcingAssetAdjustmentService } from './sourcing-asset-adjustment.service';

@Component({
    selector: 'gtw-sourcing-asset-adjustment',
    templateUrl: './sourcing-asset-adjustment.component.html',
    styleUrls: ['./sourcing-asset-adjustment.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SourcingAssetAdjustmentComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Standard component properties
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Modal title
  modalTitle = "Add Adjustment";

  // Form data
  entity: any = undefined;
  adjustmentType: any = undefined;
  assetFLC: any = undefined;
  basket: any = undefined;
  fdii: any = undefined;
  adjustmentAmount: string = '';
  comments: string = '';

  // Dropdown data
  entityDetails: any[] = [];
  adjustmentTypeDetails: any[] = [];
  assetFLCDetails: any[] = [];
  basketDetails: any[] = [];
  fdiiDetails: any[] = [];

  // Transaction type mapping
  transTypeObj: any = {'BOY':'AST_BOY_ADJ','EOY':'AST_EOY_ADJ'};

  // Component state
  isCreate = true;
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  
  // Loading states
  showFormLoader = false;
  entitiesLoading = false;
  dropdownLoading = false;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private sourcingAssetAdjustmentService: SourcingAssetAdjustmentService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
  }

  init() {
    try {
      const data = this.rowData || {};
      this.isCreate = !data.COMBINATION_KEY || data.COMBINATION_KEY === '' || data.COMBINATION_KEY === null;
      this.modalTitle = this.isCreate ? "Add Adjustment" : "Edit Adjustment";

      // CRITICAL: In CREATE mode, rowData might be empty/minimal, so check filterParams only
      const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
      const hasRowDataOrIsCreate = this.isCreate || (this.rowData && Object.keys(this.rowData).length > 0);
      
      if (hasFilterParams && hasRowDataOrIsCreate) {
        this.checkLockStatus();
        this.loadData();
      }
    } catch (e) {
      console.error('Error in SourcingAssetAdjustment init:', e);
    }
  }

  checkLockStatus() {
    const lockParams = {
      action_code: 'n10cpv',
      p_scenario: this.filterParams.scenario,
      p_jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    this.sourcingAssetAdjustmentService.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      lockParams,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject && response.jsonObject.length > 0) {
          const lockData = response.jsonObject[0];
          
          if (lockData.LOCK_STATUS === 'Y') {
            this._snackBar.open(
              `Tax Return has already been filed for tax year ${this.filterParams.tax_year}.`, 
              'Close', 
              { duration: 4000, panelClass: ['error-snackbar'] }
            );
            this.cancel();
            return;
          }
          
          if (lockData.SCENARIO_DESC === 'EXAM') {
            this._snackBar.open(
              'Create Adjustment is not available for selected Scenario.', 
              'Close', 
              { duration: 4000, panelClass: ['error-snackbar'] }
            );
            this.cancel();
            return;
          }
        }
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
      }
    );
  }

  loadData() {
    if (!this.isCreate) {
      // EDIT MODE
      this.loadEditMode();
    } else {
      // CREATE MODE
      this.loadCreateMode();
    }
  }

  loadCreateMode() {
    this.entitiesLoading = true;
    
    const params = {
      action_code: 'g9oqag',
      p_scenario: this.filterParams.scenario,
      p_jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    this.sourcingAssetAdjustmentService.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.entitiesLoading = false;
        if (response && response.jsonObject) {
          this.entityDetails = response.jsonObject;
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        this.entitiesLoading = false;
        console.error('Error loading entity details:', error);
      }
    );
  }

  loadEditMode() {
    // Load entity details first
    const entityParams = {
      action_code: 'g9oqag',
      p_scenario: this.filterParams.scenario,
      p_jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    this.sourcingAssetAdjustmentService.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      entityParams,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          this.entityDetails = response.jsonObject;
          
          // Set entity from rowData
          const existingEntity = this.entityDetails.find(e => e.KEY === this.rowData.COMBINATION_KEY);
          this.entity = existingEntity || undefined;
          
          // Load dropdown data after entity is set
          this.autoLoadData();
        }
      },
      (error: any) => {
        console.error('Error loading entity details for edit:', error);
      }
    );
  }

  autoLoadData() {
    // Load Asset FLC Details
    const assetFLCParams = {
      action_code: 'gb3fr2',
      p_scenario: this.filterParams.scenario,
      p_jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    this.sourcingAssetAdjustmentService.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      assetFLCParams,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          this.assetFLCDetails = response.jsonObject;
          
          if (this.assetFLCDetails.length === 0) {
            this.assetFLCDetails = response.jsonObject.filter((data: any) => data.GIE_TYPE === 'ASSET');
          }
          
          // Set existing value in edit mode
          if (!this.isCreate && this.rowData.GIE_DESC) {
            const existingAssetFLC = this.assetFLCDetails.find((detail: any) => 
              detail.GIE_DESC.toLowerCase() === this.rowData.GIE_DESC.toLowerCase()
            );
            this.assetFLC = existingAssetFLC || {};
          }
        }
      },
      (error: any) => {
        console.error('Error loading asset FLC details:', error);
      }
    );

    // Load other dropdown data
    const dropdownParams = {
      action_code: 'dbty56',
      cc_key: this.rowData.COMBINATION_KEY,
      p_scenario: this.filterParams.scenario,
      p_jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    this.sourcingAssetAdjustmentService.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      dropdownParams,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          const collection = response.jsonObject;
          
          // Filter adjustment type details
          this.adjustmentTypeDetails = collection.filter((data: any) => data.CODE_GROUP === 'ASSET_BOY_EOY');
          
          // Filter basket details
          this.basketDetails = collection.filter((data: any) => data.CODE_GROUP === 'SOURCE_CODE_NEW');
          
          // Filter FDII details
          this.fdiiDetails = collection.filter((data: any) => data.CODE_GROUP === 'FDII_CODE');
          
          // Set existing values in edit mode
          if (!this.isCreate) {
            this.setExistingValuesForEdit();
          }
        }
      },
      (error: any) => {
        console.error('Error loading dropdown data:', error);
      }
    );
  }

  setExistingValuesForEdit() {
    // Set adjustment type
    const existingAdjType = this.adjustmentTypeDetails.find(item => 
      item.CODE_DESC === this.rowData.AST_BOY_EOY
    );
    this.adjustmentType = existingAdjType || {};
    
    // Set basket
    const existingBasket = this.basketDetails.find(item => 
      item.CODE_DESC === this.rowData.BASKET
    );
    this.basket = existingBasket || {};
    
    // Set FDII
    const existingFdii = this.fdiiDetails.find(item => 
      item.CODE_SHORT_DESC === this.rowData.FDII
    );
    this.fdii = existingFdii || {};
    
    // Set adjustment amount and comments
    this.adjustmentAmount = this.rowData.AMOUNT || '';
    this.comments = this.rowData.COMMENTS || '';
  }

  assetFLCData() {
    const params = {
      action_code: 'gb3fr2',
      p_scenario: this.filterParams.scenario,
      p_jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    this.sourcingAssetAdjustmentService.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          this.assetFLCDetails = response.jsonObject;
          this.adjustmentTypeDetails = response.jsonObject.filter((data: any) => 
            data.CODE_GROUP === 'ASSET_BOY_EOY'
          );
        }
      },
      (error: any) => {
        console.error('Error loading asset FLC data:', error);
      }
    );
  }

  getAllData() {
    if (!this.entity || !this.entity.KEY) {
      return;
    }

    const params = {
      action_code: 'dbty56',
      cc_key: this.entity.KEY,
      p_scenario: this.filterParams.scenario,
      p_jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    this.sourcingAssetAdjustmentService.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          const collection = response.jsonObject;
          
          this.adjustmentTypeDetails = collection.filter((data: any) => 
            data.CODE_GROUP === 'ASSET_BOY_EOY'
          );
          
          this.basketDetails = collection.filter((data: any) => 
            data.CODE_GROUP === 'SOURCE_CODE_NEW'
          );
          
          this.fdiiDetails = collection.filter((data: any) => 
            data.CODE_GROUP === 'FDII_CODE'
          );
        }
      },
      (error: any) => {
        console.error('Error loading all data:', error);
      }
    );
  }

  onEntityChange() {
    if (this.entity && this.entity.KEY) {
      this.assetFLCData();
      this.getAllData();
    }
  }

  settingAdjustmentAmount(event: any) {
    if (this.adjustmentAmount) {
      this.adjustmentAmount = this.adjustmentAmount.replace(/(?!^-)[^0-9\.]/g, '');
      const amount = this.adjustmentAmount;
      
      if (amount.indexOf('.') !== -1) {
        const amountSp = amount.split('.');
        let bPre = amountSp[0];
        let aPre = amountSp[1];
        
        if (bPre && bPre.length > 20) {
          bPre = bPre.substring(0, 19);
        }
        if (aPre && aPre.length > 2) {
          aPre = aPre.substring(0, 2);
        }
        this.adjustmentAmount = bPre + "." + aPre;
      } else if (amount.length > 20) {
        this.adjustmentAmount = amount.substring(0, amount.length - 1);
      }
    }
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    let returnObj: any = {};
    let transactionDetails: any[] = [];
    let errorInfo = '';

    // Validation
    if (this.entity === undefined || (this.entity && this.entity.KEY === undefined) ||
        this.adjustmentType === undefined || (this.adjustmentType && this.adjustmentType.CODE_KEY === undefined) ||
        this.assetFLC === undefined || (this.assetFLC && this.assetFLC.GIE_KEY === undefined) ||
        ((this.basket === undefined || (this.basket && this.basket.CODE_KEY === undefined) || this.basket == null) &&
         (this.fdii === undefined || (this.fdii && this.fdii.CODE_KEY === undefined) || this.fdii == null)) ||
        this.adjustmentAmount === undefined || this.adjustmentAmount === null || this.adjustmentAmount === '' ||
        (this.adjustmentType && this.adjustmentType.CODE_SHORT_DESC === 'Other' && 
         (this.comments === null || this.comments === undefined || this.comments === ""))) {
      errorInfo = "Please select all required fields";
      this._snackBar.open(errorInfo, 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      this.isSaveClicked = false;
      this.crudLoading = false;
      return;
    }

    if ((this.basket && this.basket.CODE_KEY !== undefined) && (this.fdii && this.fdii.CODE_KEY !== undefined)) {
      errorInfo = "Please select either Basket or FDII";
      this._snackBar.open(errorInfo, 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      this.isSaveClicked = false;
      this.crudLoading = false;
      return;
    }

    if (this.adjustmentAmount !== undefined && parseFloat(this.adjustmentAmount) === 0) {
      errorInfo = "Adjustment amount should not be 0";
      this._snackBar.open(errorInfo, 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      this.isSaveClicked = false;
      this.crudLoading = false;
      return;
    }

    const otherInfo = {
      sso_id: this.USER_SETTINGS.user?.sso_id || '',
      SCREEN_KEY: this.globalService.screen_key,
      tax_year: this.filterParams.tax_year || this.globalService.tax_year,
      scenario: this.filterParams.scenario || this.globalService.scenario,
      jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    returnObj["ENTITY"] = this.entity.KEY;
    returnObj["COMBINATION_KEY"] = this.entity.KEY;
    returnObj["AST_BOY_EOY"] = this.adjustmentType.CODE_KEY;
    returnObj["GIE_KEY"] = this.assetFLC.GIE_KEY;
    
    if (this.basket && this.basket.CODE_KEY !== undefined) {
      returnObj["SOURCE_KEY"] = this.basket.CODE_KEY;
    }
    
    if (this.fdii && this.fdii.CODE_KEY !== undefined) {
      returnObj["FDII_KEY"] = this.fdii.CODE_KEY;
    }
    
    returnObj["AMOUNT"] = this.adjustmentAmount;
    returnObj["COMMENTS"] = this.comments;
    returnObj["IS_SINGLE"] = 'S';
    returnObj["TRANS_TYPE"] = this.transTypeObj[this.adjustmentType['CODE_SHORT_DESC']];
    
    if (!this.isCreate) {
      returnObj["ADJUSTMENT_KEY"] = this.rowData.ADJ_KEY;
      returnObj["GROUP_KEY"] = this.rowData.GROUP_KEY;
    }
    
    returnObj["OPERATION_TYPE"] = this.isCreate ? 'I' : 'U';
    transactionDetails.push(returnObj);

    const checkLockObj = [{combination_key: this.entity.KEY}];
    const LockObj = {
      modal_name: 'assetAdjustmentsFTC',
      lockingObj: checkLockObj
    };

    this.sourcingAssetAdjustmentService.saveSourcingAssetAdjustment(
      this.GENERAL_CONFIG.base_url,
      returnObj,
      transactionDetails,
      otherInfo,
      LockObj,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Sourcing Asset Adjustment has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  reset() {
    // Clear all form fields
    this.entity = undefined;
    this.adjustmentType = undefined;
    this.assetFLC = undefined;
    this.basket = undefined;
    this.fdii = undefined;
    this.adjustmentAmount = '';
    this.comments = '';
    
    // Reload data (matches AngularJS behavior)
    if (!this.isCreate) {
      // EDIT mode: Reload entity from entityDetails and repopulate form
      if (this.entityDetails && this.entityDetails.length > 0) {
        const existingEntity = this.entityDetails.find(e => e.KEY === this.rowData.COMBINATION_KEY);
        this.entity = existingEntity || undefined;
      }
      this.autoLoadData();
    }
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  compareByKey(item1: any, item2: any): boolean {
    if (item1 === undefined && item2 === undefined) return true;
    if (item1 === null && item2 === null) return true;
    if (!item1 || !item2) return false;
    return item1.KEY === item2.KEY;
  }

  compareByCodeKey(item1: any, item2: any): boolean {
    if (item1 === undefined && item2 === undefined) return true;
    if (item1 === null && item2 === null) return true;
    if (!item1 || !item2) return false;
    return item1.CODE_KEY === item2.CODE_KEY;
  }

  compareByGieKey(item1: any, item2: any): boolean {
    if (item1 === undefined && item2 === undefined) return true;
    if (item1 === null && item2 === null) return true;
    if (!item1 || !item2) return false;
    return item1.GIE_KEY === item2.GIE_KEY;
  }
}


